/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import moment from 'moment';

import { AuthContext } from './Auth/AuthContext';
import usePlans from '../hooks/usePlans';
import api from '../services/api';
import { TRIAL_EXPIRING_SOON_DAYS } from '../types/company';

const defaultState = {
  loading: true,
  planActive: true,
  // trial — derivado de user.trial (SerializeUser do backend)
  trialActive: false,
  trialDaysLeft: 0,
  trialExpiresAt: null,
  trialExpiringSoon: false,
  // features do plano
  campaigns: false,
  schedules: false,
  openAi: false,
  internalChat: false,
  integrations: false,
  whatsappOfficial: false,
  facebook: false,
  instagram: false,
  // apps da empresa — slugs dos apps ativos (status=active, sem accessBlocked)
  activeAppSlugs: new Set(),
};

const PlanPermissionsContext = createContext({
  ...defaultState,
  canAccess: () => false,
  hasApp: () => false,
  refetchPlanPermissions: () => Promise.resolve(),
});

/**
 * Deriva o estado de trial diretamente de user.trial (retornado por /auth/me).
 * Não depende mais de plan.trialDays nem de cálculos locais de dueDate.
 */
const extractTrialState = (userTrial) => {
  if (!userTrial || typeof userTrial !== 'object') {
    return {
      trialActive: false,
      trialDaysLeft: 0,
      trialExpiresAt: null,
      trialExpiringSoon: false,
    };
  }

  // Suporta tanto o formato novo (inTrial/daysRemaining) quanto o antigo (active/daysLeft)
  const active = Boolean(userTrial.inTrial ?? userTrial.active);
  const daysLeft =
    userTrial.daysRemaining ?? (typeof userTrial.daysLeft === 'number' ? userTrial.daysLeft : 0);
  const expiresAt = userTrial.expiresAt ?? null;
  const expiringSoon = active && daysLeft <= TRIAL_EXPIRING_SOON_DAYS;

  return {
    trialActive: active,
    trialDaysLeft: daysLeft,
    trialExpiresAt: expiresAt,
    trialExpiringSoon: expiringSoon,
  };
};

/**
 * Determina se a conta está ativa com base em user.company.status.
 * Fallback 1: trial ativo (inTrial=true e não expirado).
 * Fallback 2: dueDate com 3 dias de carência (compatibilidade com backend antigo).
 */
const derivePlanActive = (isSuperAdmin, company, userTrial) => {
  if (isSuperAdmin) return true;

  const status = company?.status;

  // Backend legado envia company.status como boolean
  if (typeof status === 'boolean') return status;

  // Novo backend envia company.status como string
  if (typeof status === 'string' && status.length > 0) {
    return status === 'active' || status === 'trial';
  }

  // Fallback 1: trial ativo reportado pelo backend via user.trial
  if (userTrial?.inTrial && !userTrial?.expired) return true;

  // Fallback 2: dueDate com 3 dias de carência (comportamento anterior)
  // Mantido apenas durante período de transição do backend
  const dueDate = company?.dueDate;
  if (!dueDate) return true;

  return !moment().isAfter(moment(dueDate).add(3, 'days').endOf('day'));
};

export const PlanPermissionsProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  const [state, setState] = useState(defaultState);
  /** Incrementa para forçar novo carregamento (ex.: após ativar/desativar app na loja). */
  const [reloadTick, setReloadTick] = useState(0);
  const refetchResolveRef = useRef(null);

  const refetchPlanPermissions = useCallback(() => {
    return new Promise((resolve) => {
      refetchResolveRef.current = resolve;
      setReloadTick((t) => t + 1);
    });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let isMounted = true;

    const loadPlan = async () => {
      try {
        const isSuperAdmin = Boolean(user?.super);

        if (!user?.companyId) {
          setState(() => ({
            ...defaultState,
            loading: false,
            campaigns: isSuperAdmin,
            schedules: isSuperAdmin,
            openAi: isSuperAdmin,
            internalChat: isSuperAdmin,
            integrations: isSuperAdmin,
            whatsappOfficial: isSuperAdmin,
            facebook: isSuperAdmin,
            instagram: isSuperAdmin,
          }));
          return;
        }

        try {
          const [planConfigs, companyRes, companyAppsRes] = await Promise.all([
            getPlanCompany(undefined, user.companyId),
            api.get(`/companies/${user.companyId}`).catch(() => ({ data: null })),
            api.get(`/companies/${user.companyId}/apps`).catch(() => ({ data: [] })),
          ]);
          if (!isMounted) return;

          const plan = planConfigs?.plan || {};
          const company = companyRes?.data || user?.company;
          const companyApps = Array.isArray(companyAppsRes?.data) ? companyAppsRes.data : [];
          const activeAppSlugs = new Set(
            companyApps
              .filter((ca) => ca.status === 'active' && !ca.accessBlocked)
              .map((ca) => ca.app?.slug || ca.appExtension?.slug)
              .filter(Boolean)
          );

          const planActive = derivePlanActive(isSuperAdmin, company, user?.trial);
          const trialState = extractTrialState(user?.trial);

          // If the company has selected a real plan (plan.id is set), the trial banner
          // must not show even when backend still reports inTrial=true with days remaining.
          const effectiveTrialActive = trialState.trialActive && !plan?.id;
          const adjustedTrialState = { ...trialState, trialActive: effectiveTrialActive };

          if (planConfigs?.plan) {
            setState({
              loading: false,
              planActive,
              ...adjustedTrialState,
              campaigns: isSuperAdmin || Boolean(plan.useCampaigns),
              schedules: isSuperAdmin || Boolean(plan.useSchedules),
              openAi: isSuperAdmin || Boolean(plan.useOpenAi),
              internalChat: isSuperAdmin || Boolean(plan.useInternalChat),
              integrations: isSuperAdmin || Boolean(plan.useIntegrations),
              whatsappOfficial: isSuperAdmin || Boolean(plan.useWhatsappOfficial),
              facebook: isSuperAdmin || Boolean(plan.useFacebook),
              instagram: isSuperAdmin || Boolean(plan.useInstagram),
              activeAppSlugs,
            });
          } else {
            setState(() => ({
              ...defaultState,
              loading: false,
              planActive,
              ...adjustedTrialState,
              campaigns: isSuperAdmin,
              schedules: isSuperAdmin,
              openAi: isSuperAdmin,
              internalChat: isSuperAdmin,
              integrations: isSuperAdmin,
              whatsappOfficial: isSuperAdmin,
              facebook: isSuperAdmin,
              instagram: isSuperAdmin,
              activeAppSlugs,
            }));
          }
        } catch (err) {
          if (isMounted) {
            const isSuperAdmin = Boolean(user?.super);
            setState(() => ({
              ...defaultState,
              loading: false,
              campaigns: isSuperAdmin,
              schedules: isSuperAdmin,
              openAi: isSuperAdmin,
              internalChat: isSuperAdmin,
              integrations: isSuperAdmin,
              whatsappOfficial: isSuperAdmin,
              facebook: isSuperAdmin,
              instagram: isSuperAdmin,
            }));
          }
        }
      } finally {
        const resolveRefetch = refetchResolveRef.current;
        refetchResolveRef.current = null;
        resolveRefetch?.();
      }
    };

    loadPlan();

    return () => {
      isMounted = false;
    };
  }, [
    user?.companyId,
    user?.company?.status,
    user?.company?.dueDate,
    user?.trial,
    user?.super,
    reloadTick,
  ]);

  const value = useMemo(() => {
    const isSuperAdmin = Boolean(user?.super);

    const canAccess = (featureKey) => {
      if (isSuperAdmin) return true;
      if (!featureKey) return true;
      return Boolean(state[featureKey]);
    };

    const hasApp = (slug) => isSuperAdmin || state.activeAppSlugs.has(slug);

    return { ...state, canAccess, hasApp, refetchPlanPermissions };
  }, [state, user?.super, refetchPlanPermissions]);

  return (
    <PlanPermissionsContext.Provider value={value}>{children}</PlanPermissionsContext.Provider>
  );
};

export const usePlanPermissions = () => {
  return useContext(PlanPermissionsContext);
};

export default PlanPermissionsContext;
